import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/client';

const PROTOCOL_OPTIONS = [
  { value: 'shoutcast', label: 'SHOUTcast' },
  { value: 'icecast', label: 'Icecast' },
  { value: 'srt', label: 'SRT' },
  { value: 'rtmp', label: 'RTMP' },
];

export default function ServerForm({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = editMode && Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    protocol: 'shoutcast',
    host: '',
    port: 8000,
    mount: '',
    username: '',
    password: '',
    max_listeners: 100,
    bitrate: 128,
    format: 'mp3',
    is_default: false,
    is_active: true,
  });

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      fetchServer();
    }
  }, [id]);

  const fetchServer = async () => {
    try {
      const { data } = await api.get(`/api/streaming/servers/${id}`);
      setFormData({
        name: data.name || '',
        protocol: data.protocol || 'shoutcast',
        host: data.host || '',
        port: data.port || 8000,
        mount: data.mount || '',
        username: data.username || '',
        password: data.password || '',
        max_listeners: data.max_listeners || 100,
        bitrate: data.bitrate || 128,
        format: data.format || 'mp3',
        is_default: data.is_default ?? false,
        is_active: data.is_active ?? true,
      });
    } catch (err) {
      console.error('[ServerForm] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value, 10) || '' : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Server name is required';
    if (!formData.host.trim()) newErrors.host = 'Host is required';
    if (!formData.port) newErrors.port = 'Port is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/streaming/servers/${id}`, formData);
      } else {
        await api.post('/api/streaming/servers', formData);
      }
      navigate('/streaming');
    } catch (err) {
      console.error('[ServerForm] Save failed:', err);
      if (err.response?.data?.message) {
        setErrors({ submit: err.response.data.message });
      } else {
        setErrors({ submit: 'Failed to save server.' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading server...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {isEdit ? 'Edit Server' : 'Add Server'}
        </h1>
        <Link to="/streaming" className="btn btn-secondary btn-sm">
          Back to Servers
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`input ${errors.name ? 'border-error-500' : ''}`}
              placeholder="Friendly name"
            />
            {errors.name && <p className="text-xs text-error-400 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Protocol</label>
            <select name="protocol" value={formData.protocol} onChange={handleChange} className="input">
              {PROTOCOL_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Host *</label>
            <input
              type="text"
              name="host"
              value={formData.host}
              onChange={handleChange}
              className={`input ${errors.host ? 'border-error-500' : ''}`}
              placeholder="e.g. live.example.com"
            />
            {errors.host && <p className="text-xs text-error-400 mt-1">{errors.host}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Port *</label>
            <input
              type="number"
              name="port"
              value={formData.port}
              onChange={handleChange}
              className={`input ${errors.port ? 'border-error-500' : ''}`}
              min="1"
              max="65535"
            />
            {errors.port && <p className="text-xs text-error-400 mt-1">{errors.port}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Mount Point</label>
            <input
              type="text"
              name="mount"
              value={formData.mount}
              onChange={handleChange}
              className="input"
              placeholder="/stream (Icecast only)"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Bitrate (kbps)</label>
              <select name="bitrate" value={formData.bitrate} onChange={handleChange} className="input">
                {[32, 48, 64, 96, 128, 192, 256, 320].map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Format</label>
              <select name="format" value={formData.format} onChange={handleChange} className="input">
                {['mp3', 'aac', 'ogg', 'flac'].map((f) => (
                  <option key={f} value={f}>{f.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="input"
              placeholder="Source username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="input"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Max Listeners</label>
            <input
              type="number"
              name="max_listeners"
              value={formData.max_listeners}
              onChange={handleChange}
              className="input"
              min="1"
              placeholder="0 for unlimited"
            />
          </div>

          <div className="flex items-center gap-3 pt-6">
            <input
              type="checkbox"
              id="is_default"
              name="is_default"
              checked={formData.is_default}
              onChange={handleChange}
              className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
            />
            <label htmlFor="is_default" className="text-sm text-dark-300">Set as default server</label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
            />
            <label htmlFor="is_active" className="text-sm text-dark-300">Active</label>
          </div>
        </div>
      </form>

      <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
        <Link to="/streaming" className="btn btn-secondary">
          Cancel
        </Link>
        <button type="submit" disabled={saving} className="btn btn-primary">
          {saving ? 'Saving...' : isEdit ? 'Update Server' : 'Create Server'}
        </button>
      </div>
    </div>
  );
}
